/**
 * Rules Loader
 * Loads persistent instructions from .metis/rules/ directory
 * Similar to Claude Code's .claude/rules/ feature
 */

import fs from 'fs';
import path from 'path';
import os from 'os';

export interface RuleFile {
  name: string;
  path: string;
  content: string;
  priority: number;
  lastModified: Date;
}

export interface RulesHierarchy {
  globalRules: RuleFile[];
  projectRules: RuleFile[];
  combinedRules: string;
  totalFiles: number;
}

export class RulesLoader {
  private projectRoot: string;
  private homeDir: string;
  private cache: RulesHierarchy | null = null;
  private cacheTime: number = 0;
  private cacheTTL: number = 30000; // 30 seconds

  constructor(projectRoot: string) {
    this.projectRoot = projectRoot;
    this.homeDir = os.homedir();
  }

  /**
   * Load all rules from global and project directories
   */
  loadRules(forceRefresh: boolean = false): RulesHierarchy {
    const now = Date.now();

    if (!forceRefresh && this.cache && (now - this.cacheTime) < this.cacheTTL) {
      return this.cache;
    }

    const globalRules = this.loadRulesFromDirectory(
      path.join(this.homeDir, '.metis', 'rules'),
      'global'
    );

    const projectRules = this.loadRulesFromDirectory(
      path.join(this.projectRoot, '.metis', 'rules'),
      'project'
    );

    // Combine rules with proper ordering
    // Global rules come first, then project rules (project can override/extend)
    const combinedRules = this.combineRules([...globalRules, ...projectRules]);

    this.cache = {
      globalRules,
      projectRules,
      combinedRules,
      totalFiles: globalRules.length + projectRules.length,
    };
    this.cacheTime = now;

    return this.cache;
  }

  /**
   * Load rules from a specific directory
   */
  private loadRulesFromDirectory(rulesDir: string, level: string): RuleFile[] {
    const rules: RuleFile[] = [];

    if (!fs.existsSync(rulesDir)) {
      return rules;
    }

    try {
      const files = fs.readdirSync(rulesDir)
        .filter(f => f.endsWith('.md') || f.endsWith('.txt'))
        .sort(); // Sort alphabetically for consistent ordering

      for (const file of files) {
        const filePath = path.join(rulesDir, file);

        try {
          const stats = fs.statSync(filePath);
          if (!stats.isFile()) continue;

          const content = fs.readFileSync(filePath, 'utf8');
          const priority = this.extractPriority(file, content);

          rules.push({
            name: file,
            path: filePath,
            content: content.trim(),
            priority,
            lastModified: stats.mtime,
          });
        } catch (error) {
          console.warn(`Failed to load rule file ${filePath}:`, error);
        }
      }

      // Sort by priority (lower number = higher priority)
      rules.sort((a, b) => a.priority - b.priority);

    } catch (error) {
      console.warn(`Failed to read rules directory ${rulesDir}:`, error);
    }

    return rules;
  }

  /**
   * Extract priority from filename or content
   * Files can be prefixed with numbers: 01-coding-standards.md
   * Or have a priority in frontmatter
   */
  private extractPriority(filename: string, content: string): number {
    // Check filename prefix (e.g., "01-coding-standards.md")
    const match = filename.match(/^(\d+)-/);
    if (match) {
      return parseInt(match[1], 10);
    }

    // Check frontmatter for priority
    const frontmatterMatch = content.match(/^---\s*\n[\s\S]*?priority:\s*(\d+)[\s\S]*?\n---/);
    if (frontmatterMatch) {
      return parseInt(frontmatterMatch[1], 10);
    }

    // Default priority
    return 100;
  }

  /**
   * Combine rules into a single string
   */
  private combineRules(rules: RuleFile[]): string {
    if (rules.length === 0) {
      return '';
    }

    const sections: string[] = [];

    for (const rule of rules) {
      // Skip empty rules
      if (!rule.content.trim()) continue;

      // Remove frontmatter from content
      let content = rule.content;
      if (content.startsWith('---')) {
        const endMatch = content.indexOf('---', 3);
        if (endMatch !== -1) {
          content = content.substring(endMatch + 3).trim();
        }
      }

      // Add section header with rule name
      const header = `## ${this.formatRuleName(rule.name)}`;
      sections.push(`${header}\n\n${content}`);
    }

    return sections.join('\n\n---\n\n');
  }

  /**
   * Format rule filename into readable name
   */
  private formatRuleName(filename: string): string {
    return filename
      .replace(/^\d+-/, '') // Remove number prefix
      .replace(/\.(md|txt)$/, '') // Remove extension
      .replace(/-/g, ' ') // Replace dashes with spaces
      .replace(/\b\w/g, c => c.toUpperCase()); // Title case
  }

  /**
   * Get combined rules as a string (for inclusion in system prompt)
   */
  getCombinedRules(): string {
    const hierarchy = this.loadRules();
    return hierarchy.combinedRules;
  }

  /**
   * Check if any rules exist
   */
  hasRules(): boolean {
    const hierarchy = this.loadRules();
    return hierarchy.totalFiles > 0;
  }

  /**
   * Get rule file list for display
   */
  getRulesList(): { name: string; path: string; level: string }[] {
    const hierarchy = this.loadRules();
    const list: { name: string; path: string; level: string }[] = [];

    for (const rule of hierarchy.globalRules) {
      list.push({ name: rule.name, path: rule.path, level: 'global' });
    }

    for (const rule of hierarchy.projectRules) {
      list.push({ name: rule.name, path: rule.path, level: 'project' });
    }

    return list;
  }

  /**
   * Create a new rule file
   */
  createRule(name: string, content: string, global: boolean = false): string {
    const rulesDir = global
      ? path.join(this.homeDir, '.metis', 'rules')
      : path.join(this.projectRoot, '.metis', 'rules');

    // Ensure directory exists
    if (!fs.existsSync(rulesDir)) {
      fs.mkdirSync(rulesDir, { recursive: true });
    }

    // Ensure filename has extension
    const filename = name.endsWith('.md') ? name : `${name}.md`;
    const filePath = path.join(rulesDir, filename);

    fs.writeFileSync(filePath, content, 'utf8');

    // Clear cache
    this.clearCache();

    return filePath;
  }

  /**
   * Delete a rule file
   */
  deleteRule(name: string, global: boolean = false): boolean {
    const rulesDir = global
      ? path.join(this.homeDir, '.metis', 'rules')
      : path.join(this.projectRoot, '.metis', 'rules');

    const filename = name.endsWith('.md') ? name : `${name}.md`;
    const filePath = path.join(rulesDir, filename);

    if (fs.existsSync(filePath)) {
      fs.unlinkSync(filePath);
      this.clearCache();
      return true;
    }

    return false;
  }

  /**
   * Clear cache to force reload
   */
  clearCache(): void {
    this.cache = null;
    this.cacheTime = 0;
  }

  /**
   * Generate rules context for system prompt
   */
  generateRulesContext(): string {
    const hierarchy = this.loadRules();

    if (hierarchy.totalFiles === 0) {
      return '';
    }

    return `
<project-rules>
The following rules and guidelines have been configured for this project:

${hierarchy.combinedRules}
</project-rules>
`.trim();
  }
}

// Global rules loader instance
let globalRulesLoader: RulesLoader | null = null;

export function getRulesLoader(projectRoot?: string): RulesLoader {
  if (!globalRulesLoader) {
    globalRulesLoader = new RulesLoader(projectRoot || process.cwd());
  }
  return globalRulesLoader;
}

export function resetRulesLoader(): void {
  globalRulesLoader = null;
}
